import React from "react";

import { useLiveUpdates } from "@hooks/useLiveUpdates";
import { IdTooltip } from "@src/components/elements/IdTooltip";
import RichText from "@src/components/elements/RichText";
import { buildThemeClasses } from "@src/utils/theme";
import { getContentfulPreviewPropsFn } from "@utils";
import clsx from "clsx";
import { graphql } from "gatsby";

export function SectionBenefits({ entry: rawEntry }) {
  const entry = useLiveUpdates(rawEntry);
  const getPreviewProps = getContentfulPreviewPropsFn(entry?.contentful_id);
  const content = !entry?.content?.json
    ? entry?.content
    : { raw: JSON.stringify(entry.content?.json) };

  return (
    <section
      className={clsx("font-atkinson py-section", {...buildThemeClasses(entry?.customTheme)})}
      data-theme={entry?.customTheme}
      id={entry?.contentful_id}
    >
      <IdTooltip id={entry?.contentful_id} />
      <div className="inner grid md:grid-cols-2 gap-5 md:gap-20">
        <div className="flex flex-col gap-4">
          {entry?.headline && (
            <h2
              className={clsx(
                "text-rt-l md:text-rt-xl leading-none",
              )}
              {...getPreviewProps("headline")}
            >
              {entry?.headline}
            </h2>
          )}

          {content && (
            <div>
              <div {...getPreviewProps("content")}>
                <RichText
                  bodyRichText={content}
                  classes={clsx(
                    "space-y-4 md:text-18px",
                  )}
                  useHeadings
                />
              </div>
              {entry?.benefitsCta && (
                <div className="hidden md:flex mt-8" {...getPreviewProps("benefitsCta")}>
                  <a
                    className={clsx(
                      "btn-new min-w-200px justify-center",
                    )}
                    href={entry?.benefitsCta?.url}
                  >
                    <span {...getContentfulPreviewPropsFn(entry?.benefitsCta?.contentful_id)("benefitsCta")}>{entry?.benefitsCta?.label}</span>
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        <div
          className={clsx(
            "px-5 py-12 md:px-12  rounded-2xl grid grid-cols-2 gap-12 self-start theme--knockout",
          )}
          {...getPreviewProps("values")}
        >
          {entry?.values &&
            entry.values.map((item) => (
              <div key={item.id}>
                <p
                  className={clsx(
                    "text-rt-2l md:text-rt-xl leading-none",
                  )}
                >
                  {item.value}
                </p>
                <p className={clsx("text-18px")}>
                  {item.key}
                </p>
              </div>
            ))}
        </div>
        {entry?.benefitsCta && (
          <div className="md:hidden flex justify-center" {...getPreviewProps("benefitsCta")}>
            <a
              className={clsx(
                "btn-new min-w-200px justify-center",
              )}
              href={entry?.benefitsCta?.url}
            >
              <span {...getContentfulPreviewPropsFn(entry?.benefitsCta?.contentful_id)("benefitsCta")}>{entry?.benefitsCta?.label}</span>
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

export const SectionBenefitsFragment = graphql`
  fragment SectionBenefits on ContentfulSectionBenefits {
    contentful_id
    __typename
    sys {
      id
    }
    customTheme
    headline
    content {
      raw
    }
    values {
      value
      key
      id
    }
    benefitsCta {
      contentful_id
      __typename
      label
      url
    }
  }
`;
